import { useShellCreateAction } from '@ordo/ui/app-shell'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { type Column, DataGrid } from 'compositions/DataGrid'
import { Page } from 'compositions/Page'
import { ShowInactiveToggle } from 'compositions/ShowInactiveToggle'
import { useSchedules } from 'hooks/api'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { Schedule } from 'types/schedules'

function formatTimestamp(epoch: number | null): string {
  if (epoch === null) {
    return '—'
  }
  return new Date(epoch).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatRelative(epoch: number | null): string {
  if (epoch === null) {
    return ''
  }
  const diff = epoch - Date.now()
  if (diff < 0) {
    return '(overdue)'
  }
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60) {
    return `(in ${minutes}m)`
  }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `(in ${hours}h)`
  }
  const days = Math.floor(hours / 24)
  return `(in ${days}d)`
}

const COLUMNS: Column<Schedule>[] = [
  {
    key: 'communicationName',
    header: 'Communication',
    render: (row) => (
      <Link to={`/schedule/${row.id}`} className="text-foreground hover:underline">
        {row.scheduledCommunication.name}
      </Link>
    ),
  },
  {
    key: 'communicationMethod',
    header: 'Method',
    render: (row) => (
      <Badge variant="secondary" className="text-xs">
        {row.scheduledCommunication.method}
      </Badge>
    ),
  },
  {
    key: 'lastRun',
    header: 'Last Run',
    mobile: 'hidden',
    render: (row) => (
      <span className="text-muted-foreground">
        {formatTimestamp(row.lastRun ? new Date(row.lastRun).getTime() : null)}
      </span>
    ),
  },
  {
    key: 'nextExecutionTime',
    header: 'Next Run',
    sortable: true,
    render: (row) => (
      <span>
        {formatTimestamp(row.nextExecutionTime)}{' '}
        <span className="text-muted-foreground">{formatRelative(row.nextExecutionTime)}</span>
      </span>
    ),
  },
  {
    key: 'active',
    header: 'Status',
    mobile: 'aside',
    render: (row) => {
      if (!row.active) {
        return (
          <Badge variant="secondary" className="text-xs">
            Inactive
          </Badge>
        )
      }
      if (row.pausedUntil) {
        const until = new Date(row.pausedUntil)
        if (until.getTime() > Date.now()) {
          return (
            <Badge variant="outline" className="text-xs">
              Paused until{' '}
              {until.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </Badge>
          )
        }
      }
      return (
        <Badge variant="default" className="text-xs">
          Active
        </Badge>
      )
    },
  },
]

export function ScheduleList() {
  const navigate = useNavigate()
  // On phones "New Schedule" is the shell's floating + button; the header button is desktop only.
  useShellCreateAction({ label: 'New Schedule', onCreate: () => navigate('/schedule/new') })
  const [includeInactive, setIncludeInactive] = useState(false)
  const { data: rawSchedules, loading } = useSchedules({ includeInactive })

  // Sort by next execution time (soonest first, nulls last)
  const schedules = useMemo(() => {
    if (!rawSchedules) {
      return []
    }
    return [...rawSchedules].sort((a, b) => {
      if (a.nextExecutionTime === null && b.nextExecutionTime === null) {
        return 0
      }
      if (a.nextExecutionTime === null) {
        return 1
      }
      if (b.nextExecutionTime === null) {
        return -1
      }
      return a.nextExecutionTime - b.nextExecutionTime
    })
  }, [rawSchedules])

  return (
    <Page>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Schedules</h1>
        <div className="flex items-center gap-4">
          <ShowInactiveToggle checked={includeInactive} onCheckedChange={setIncludeInactive} />
          <Button asChild className="hidden md:inline-flex">
            <Link to="/schedule/new">
              <Plus className="h-4 w-4" />
              New Schedule
            </Link>
          </Button>
        </div>
      </div>
      <DataGrid
        columns={COLUMNS}
        data={schedules}
        keyField="id"
        pageSize={20}
        loading={loading}
        storageKey="schedule"
      />
    </Page>
  )
}
